// src/components/learning/EvaluationPostCours.js
// Pop-up d'évaluation rapide — le prof évalue l'élève après le cours
// S'affiche automatiquement 2 minutes après la fin de la session
// Maximum 2 minutes à remplir

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const LETTRES_ABJAD = [
  'ا','ب','ت','ث','ج','ح','خ','د','ذ','ر','ز','س','ش','ص','ض','ط','ظ','ع','غ','ف','ق','ك','ل','م','ن','ه','و','ي'
];

export default function EvaluationPostCours({ token, reservation, onClose, onSaved }) {
  const [lettre, setLettre] = useState('');
  const [reconnue, setReconnue] = useState(null);
  const [prononciation, setPrononciation] = useState(null);
  const [ecriture, setEcriture] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes

  // Countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  const handleSave = async () => {
    if (!lettre) return;
    setSaving(true);
    try {
      await axios.post(`${API}/progression/evaluation`, {
        eleve_id: reservation?.eleve_id || reservation?.eleve?.created_by,
        reservation_id: reservation?.id,
        lettre_travaillee: lettre,
        lettre_reconnue: reconnue === true,
        prononciation_correcte: prononciation === true,
        peut_ecrire: ecriture === true,
        commentaire,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      if (onSaved) onSaved();
      setTimeout(onClose, 1500);
    } catch (e) {
      console.error('Erreur évaluation:', e);
    } finally {
      setSaving(false);
    }
  };

  const C = {
    green: '#065f46', greenLight: '#E1F5EE', greenMid: '#1D9E75',
    greenBorder: '#a7f3d0', text: '#1a1a1a', muted: '#6b7280',
    white: '#fff', yellow: '#f59e0b', yellowLight: '#fffbeb',
  };

  if (saved) return (
    <div style={overlay}>
      <div style={{ ...box, textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.green }}>
          Évaluation enregistrée !
        </div>
        <div style={{ fontSize: 14, color: C.muted, marginTop: 8 }}>
          La progression de l'élève a été mise à jour
        </div>
      </div>
    </div>
  );

  return (
    <div style={overlay}>
      <div style={box}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.green}, #047857)`,
          borderRadius: '16px 16px 0 0',
          padding: '20px 24px',
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>📊 Évaluation post-cours</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
              Élève : {reservation?.eleve?.nom || reservation?.eleve?.prenom || 'Élève'}
            </div>
          </div>
          <div style={{
            background: timeLeft < 30 ? '#ef4444' : 'rgba(255,255,255,0.2)',
            borderRadius: 20, padding: '4px 12px',
            fontSize: 14, fontWeight: 700,
            transition: 'background 0.3s',
          }}>
            ⏱ {formatTime(timeLeft)}
          </div>
        </div>

        <div style={{ padding: 24 }}>

          {/* Lettre travaillée */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>
              Quelle lettre avez-vous travaillée ?
            </div>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, direction: 'rtl',
            }}>
              {LETTRES_ABJAD.map(l => (
                <div key={l} onClick={() => setLettre(l)} style={{
                  width: 40, height: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 10,
                  border: `${lettre === l ? '2px' : '1px'} solid ${lettre === l ? C.green : '#e5e7eb'}`,
                  background: lettre === l ? C.greenLight : '#f9f9f9',
                  cursor: 'pointer',
                  fontSize: 20, color: lettre === l ? C.green : C.text,
                  fontFamily: "'Amiri', serif",
                  fontWeight: lettre === l ? 700 : 400,
                  transition: 'all 0.15s',
                }}>
                  {l}
                </div>
              ))}
            </div>
          </div>

          {/* Questions évaluation */}
          {lettre && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>
                  L'élève reconnaît la lettre <span style={{ color: C.green, fontSize: 18, fontFamily: "'Amiri', serif" }}>{lettre}</span> ?
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { val: true, label: '✅ Oui', bg: reconnue === true ? '#d1fae5' : '#f9f9f9', border: reconnue === true ? C.green : '#e5e7eb', color: reconnue === true ? C.green : C.muted },
                    { val: false, label: '❌ Non', bg: reconnue === false ? '#fee2e2' : '#f9f9f9', border: reconnue === false ? '#ef4444' : '#e5e7eb', color: reconnue === false ? '#dc2626' : C.muted },
                  ].map(opt => (
                    <button key={String(opt.val)} onClick={() => setReconnue(opt.val)} style={{
                      flex: 1, padding: '10px', borderRadius: 10,
                      border: `1.5px solid ${opt.border}`,
                      background: opt.bg, color: opt.color,
                      fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>
                  Prononciation correcte ?
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { val: true, label: '✅ Oui', bg: prononciation === true ? '#d1fae5' : '#f9f9f9', border: prononciation === true ? C.green : '#e5e7eb', color: prononciation === true ? C.green : C.muted },
                    { val: false, label: '❌ Non', bg: prononciation === false ? '#fee2e2' : '#f9f9f9', border: prononciation === false ? '#ef4444' : '#e5e7eb', color: prononciation === false ? '#dc2626' : C.muted },
                  ].map(opt => (
                    <button key={String(opt.val)} onClick={() => setPrononciation(opt.val)} style={{
                      flex: 1, padding: '10px', borderRadius: 10,
                      border: `1.5px solid ${opt.border}`,
                      background: opt.bg, color: opt.color,
                      fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>
                  Peut l'écrire ?
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { val: true, label: '✅ Oui', bg: ecriture === true ? '#d1fae5' : '#f9f9f9', border: ecriture === true ? C.green : '#e5e7eb', color: ecriture === true ? C.green : C.muted },
                    { val: false, label: '❌ Non', bg: ecriture === false ? '#fee2e2' : '#f9f9f9', border: ecriture === false ? '#ef4444' : '#e5e7eb', color: ecriture === false ? '#dc2626' : C.muted },
                  ].map(opt => (
                    <button key={String(opt.val)} onClick={() => setEcriture(opt.val)} style={{
                      flex: 1, padding: '10px', borderRadius: 10,
                      border: `1.5px solid ${opt.border}`,
                      background: opt.bg, color: opt.color,
                      fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* Commentaire */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
                  Note pour le prochain cours (optionnel)
                </div>
                <textarea
                  value={commentaire}
                  onChange={e => setCommentaire(e.target.value)}
                  placeholder="Ex: Bien progressé sur les voyelles, revoir les formes..."
                  rows={2}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '1px solid #e5e7eb', borderRadius: 10,
                    fontSize: 13, resize: 'none', outline: 'none',
                    fontFamily: 'DM Sans, sans-serif',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{
              flex: 1, padding: '12px', borderRadius: 10,
              border: '1px solid #e5e7eb', background: '#f9f9f9',
              color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              Passer
            </button>
            <button onClick={handleSave} disabled={!lettre || saving} style={{
              flex: 2, padding: '12px', borderRadius: 10, border: 'none',
              background: lettre ? `linear-gradient(135deg, ${C.green}, #047857)` : '#e5e7eb',
              color: lettre ? '#fff' : '#9ca3af',
              fontSize: 13, fontWeight: 700, cursor: lettre ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}>
              {saving ? '⏳ Enregistrement...' : '✅ Enregistrer l\'évaluation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.6)', zIndex: 99999,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};

const box = {
  background: '#fff', borderRadius: 16,
  width: '100%', maxWidth: 500,
  boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
  maxHeight: '95vh', overflowY: 'auto',
};
